/**
 * LA CADENA DE INSTRUCCIÓN — qué hacer en la casa, en qué orden, y por qué.
 * =============================================================================
 *
 * Este archivo es la mitad del producto que `PRODUCT.md` no tenía hasta el 2026-08-13.
 * El motor (`riego.js`) resuelve la cadena de CÁLCULO: spec → regla → decisión → precio.
 * Esta resuelve la cadena de INSTRUCCIÓN, que es la que produce **un operador capaz**.
 *
 * Juan lo pidió así: *«que la aplicación me diga exactamente qué es lo que tengo que
 * hacer, qué tengo que medir y de qué forma, y toda la teoría detrás»*.
 *
 * ---------------------------------------------------------------------------
 * TRES REGLAS DE FORMA, y las tres vienen de los principios del producto
 *
 * 1. **La teoría se entrega donde se usa** (Principio 8). Cada paso trae `porQue`, que
 *    vive detrás de un toque. Un manual que hay que leer antes de la visita es un
 *    manual que no se lee.
 *
 * 2. **Nada supone pericia que el operador no tenga** (Principio 7). Se pregunta lo que
 *    se puede OBSERVAR —«llena la cubeta y cronometra»—, nunca lo que hay que saber
 *    para responder —«¿qué caudal necesitas?»—. La traducción es trabajo del sistema.
 *
 * 3. **Hay un guion de venta, no solo uno técnico.** `decir` es lo que el operador le
 *    dice al cliente en ese momento. No es adorno: la visita es la venta, y quien no
 *    domina el tema se queda callado justo cuando hay que hablar.
 *
 * ---------------------------------------------------------------------------
 * CÓMO SE LEE CADA PASO
 *
 *   titulo      Qué se va a hacer, en imperativo.
 *   instrumento Con qué. `null` si no hace falta ninguno.
 *   como        Los movimientos concretos, en orden. Sin teoría.
 *   porQue      La teoría, detrás de un toque. Explica la consecuencia de saltárselo.
 *   decir       Qué decirle al cliente mientras tanto. Opcional.
 *   captura     Qué dato deja este paso en el sistema, si deja alguno.
 *   ojo         La trampa concreta en la que se cae si nadie avisa. Opcional.
 * ============================================================================= */

export const GUIA_RIEGO = {
  id: 'riego',
  titulo: 'Levantamiento de riego',
  duracion: '30 a 40 minutos',
  resumen: 'Se mide el agua disponible, se traza el jardín y se coloca el riego encima. ' +
           'El cálculo de tubería y bomba sale solo al final.',

  pasos: [
    {
      id: 'toma',
      titulo: 'Localiza la toma de agua y márcala',
      instrumento: null,
      como: [
        'Pregunta dónde está la llave de la que se va a alimentar el riego.',
        'Normalmente es una llave de jardín, la cisterna, o la bajada del tinaco.',
        'Márcala en el plano: es el punto del que va a salir toda la tubería.'
      ],
      porQue:
        'Toda la red se rutea desde ahí, así que su posición cambia los metros de tubo y ' +
        'el número de codos. Moverla tres metros puede cambiar el presupuesto más que ' +
        'agregar un aspersor.',
      decir: 'Vamos a alimentar el riego de aquí, así que todo el trazo sale de este punto.',
      captura: 'Posición de la toma en el plano',
      ojo: 'Si la casa alimenta por gravedad desde un tinaco, la presión la da la altura ' +
           'del tinaco, no una bomba. Anótalo: cambia el cálculo por completo.'
    },

    {
      id: 'caudal',
      titulo: 'Mide el caudal disponible',
      instrumento: 'Cubeta de volumen conocido y cronómetro',
      como: [
        'Abre la llave por completo.',
        'Llena una cubeta de 19 litros y cronometra cuánto tarda.',
        'Repítelo tres veces y quédate con el promedio.',
        'Anota el volumen de la cubeta y los segundos: el sistema hace la cuenta.'
      ],
      porQue:
        'Es el dato que más manda de toda la visita. El caudal disponible limita cuántos ' +
        'aspersores pueden regar AL MISMO TIEMPO, y de ahí salen las zonas y el número de ' +
        'válvulas. Un jardín con poco caudal no lleva menos aspersores: lleva los mismos, ' +
        'repartidos en más zonas que riegan por turnos. Sin esta medición el sistema puede ' +
        'dibujar la cobertura pero no puede decidir el sistema.',
      decir: 'Estoy midiendo cuánta agua da su toma. De esto depende si el jardín riega de ' +
             'una sola vez o por turnos, y cuántas válvulas lleva.',
      captura: 'Caudal en litros por minuto',
      ojo: 'Mídelo a la hora de peor presión del día, normalmente por la mañana. Si mides ' +
           'a mediodía con toda la colonia sin consumo, el número queda optimista y el ' +
           'sistema se diseña de más.'
    },

    {
      id: 'presion',
      titulo: 'Mide la presión y el desnivel',
      instrumento: 'Manómetro de rosca de manguera',
      como: [
        'Enrosca el manómetro en la llave.',
        'Ábrela por completo y anota la lectura con el agua corriendo, no cerrada.',
        'Si no traes manómetro, márcalo como pendiente. No lo estimes.',
        'Mira si el jardín sube respecto a la llave. Si es plano, el desnivel es cero; ' +
        'si el punto más alto queda en una terraza o un talud, anota cuánto sube.'
      ],
      porQue:
        'El radio que anuncia un aspersor es a su presión de trabajo. Con menos presión el ' +
        'chorro no llega, quedan huecos secos, y el sistema entregado no es el que se ' +
        'dibujó. La presión decide si hace falta bomba o si basta con la de la red. El ' +
        'desnivel se suma a esa cuenta: cada metro que el agua tiene que subir se le cobra ' +
        'a la bomba igual que un metro de presión, y es lo único de todo el módulo que ' +
        'ninguna tabla puede darte — hay que verlo en la casa.',
      decir: 'La presión define hasta dónde llega cada aspersor. Con esto sabemos si su ' +
             'toma alcanza o si hay que poner una bomba.',
      captura: 'Presión en bar',
      ojo: 'La lectura con la llave cerrada es la presión estática y siempre sale más alta. ' +
           'La que sirve es con el agua corriendo.'
    },

    {
      id: 'trazo',
      titulo: 'Traza el jardín',
      instrumento: 'Flexómetro, cinta larga o medidor láser',
      como: [
        'Recorre el perímetro de la zona que se va a regar.',
        'Ve tocando las esquinas en la pantalla, en orden.',
        'Cada lado te dice cuánto mide mientras lo trazas: compáralo con el flexómetro.',
        'Si un lado no cuadra, arrastra la esquina o escribe la medida exacta abajo.',
        'Cuando estén todas, toca «Cerrar el trazo».',
        'Deja fuera lo que no se riega: banquetas, andadores, terrazas.'
      ],
      porQue:
        'El área manda cuántos aspersores caben y el perímetro manda dónde tienen que ir ' +
        'los de media luna. Un trazo con dos metros de error se arrastra hasta la lista de ' +
        'materiales, porque de la geometría salen los metros de tubo.',
      decir: 'Le voy a levantar el plano de su jardín aquí mismo. Lo que ve dibujado es su ' +
             'terreno a escala.',
      captura: 'Polígono del jardín, en metros'
    },

    {
      id: 'obstaculos',
      titulo: 'Anota lo que estorba y lo que bebe distinto',
      instrumento: null,
      como: [
        'Marca árboles, macetones fijos, asadores y todo lo que bloquee el chorro.',
        'Pregunta qué zonas son de pasto y cuáles de planta de bajo consumo.',
        'Anota si hay pendiente y hacia dónde baja.'
      ],
      porQue:
        'Un aspersor detrás de un árbol riega el árbol y deja seco lo que hay atrás. Y ' +
        'mezclar pasto con planta de bajo consumo en la misma zona condena a una de las ' +
        'dos: si riegas para el pasto, ahogas la suculenta; si riegas para la suculenta, ' +
        'se te seca el pasto. Por eso se separan en zonas distintas, no por gusto.',
      decir: 'El pasto y las plantas de poco agua no pueden ir en la misma zona. Se las ' +
             'voy a separar para que no tenga que elegir cuál se le seca.',
      captura: 'Obstáculos y tipo de planta por zona'
    },

    {
      id: 'colocar',
      titulo: 'Coloca los aspersores',
      instrumento: null,
      como: [
        'Empieza por las esquinas del jardín y sigue por las orillas.',
        'Los de la orilla van en media luna o en cuarto, apuntando hacia adentro.',
        'Rellena el centro al final.',
        'Ve mirando las manchas rojas: son lo que queda seco.',
        'Si uno quedó mal puesto, arrástralo: se recalcula todo al soltarlo.'
      ],
      porQue:
        'Se empieza por las orillas porque son la restricción: un aspersor de orilla no ' +
        'puede regar hacia la calle ni mojar la fachada del vecino, así que su posición no ' +
        'es libre. El centro sí lo es. Y los chorros deben traslaparse a propósito: el ' +
        'gasto de un aspersor cae hacia el borde de su alcance, así que si los pones justo ' +
        'a la distancia de su radio, el anillo del borde queda mal regado aunque el dibujo ' +
        'se vea completo.',
      decir: 'Lo que ve en rojo es lo que quedaría seco. Vamos a acomodarlos hasta que no ' +
             'quede nada en rojo.',
      captura: 'Modelo y posición de cada aspersor'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y revisa antes de enseñar',
      instrumento: null,
      como: [
        'Toca «Calcular tubería y bomba».',
        'Revisa que no quede cobertura en rojo.',
        'Revisa que el número de zonas tenga sentido con el caudal que mediste.',
        'Si algo salió marcado como sin calibrar, dilo en la propuesta.'
      ],
      porQue:
        'El sistema reparte los aspersores en zonas que el caudal pueda alimentar, rutea la ' +
        'tubería como árbol mínimo desde la toma, y de ahí saca metros, codos, tes, ' +
        'válvulas y la bomba. Cada número puede mostrar de qué medición salió: si un ' +
        'cliente pregunta por qué son tres zonas y no dos, la respuesta está a un toque.',
      decir: 'Su jardín necesita tantas zonas porque su toma da tantos litros por minuto. ' +
             'No es una decisión mía, es lo que da el agua que tiene.',
      captura: 'Lista de materiales y dimensionamiento'
    }
  ]
}

/* =============================================================================
   LA VISITA ELÉCTRICA — la Revisión de Seguridad convertida en software.

   Es la guía más larga y la más valiosa: sostiene el servicio que hoy es el 100% del
   ingreso previsto. Y es donde el Principio 7 se pone a prueba de verdad, porque el
   operador **no sabe** cuántos watts consume una secadora ni qué calibre lleva un
   circuito. Lo que sí sabe hacer es contar aparatos, bajar pastillas y sostener una
   pinza. De eso, y solo de eso, se le pregunta.

   `Flujos_por_Segmento.md` §3.1 ya lo decía: *no es un catálogo, es una hoja de
   medición*, y la cotización se DERIVA de los hallazgos.
   ============================================================================= */

export const GUIA_ELECTRICA = {
  id: 'electrico',
  titulo: 'Revisión de seguridad eléctrica',
  duracion: '45 a 60 minutos',
  resumen: 'Se mide la instalación, se inventaría lo que consume y se traza la planta. ' +
           'De ahí salen solos los circuitos, el balanceo, el calibre y los metros de cable.',

  pasos: [
    {
      id: 'acometida',
      titulo: 'Mira cómo entra la luz a la casa',
      instrumento: null,
      como: [
        'Busca el medidor y el interruptor principal.',
        'Cuenta los cables gruesos que llegan: dos y un neutro es una fase; tres y un neutro son dos fases.',
        'Anota el amperaje que trae escrito el interruptor principal.',
        'Fotografía el medidor con su número visible.'
      ],
      porQue:
        'Es el techo de todo lo demás. Con una fase solo hay 127 V y no se puede alimentar ' +
        'secadora, parrilla de inducción ni cargador de auto. Con dos fases hay 220 V y ' +
        'además se puede BALANCEAR, que es repartir los circuitos entre las dos para que ' +
        'la casa aproveche toda su capacidad en vez de la mitad.',
      decir: 'Voy a ver con cuánta capacidad entra la luz a su casa. De aquí sale si lo que ' +
             'quiere hacer cabe, o si hay que pedirle más a la compañía.',
      captura: 'Fases, amperaje del principal, número de medidor',
      ojo: 'Si el cliente quiere parrilla de inducción, secadora o cargador de auto y solo ' +
           'hay una fase, dilo AHÍ MISMO. Descubrirlo en la instalación tira la obra.'
    },

    {
      id: 'tablero',
      titulo: 'Fotografía y transcribe el tablero',
      instrumento: 'Cámara y linterna',
      como: [
        'Fotografía el tablero abierto, de frente y completo.',
        'Cuenta los espacios totales y los que están libres.',
        'Anota el amperaje de cada pastilla, de arriba abajo.',
        'Mira si hay una barra de tierra separada de la de neutro.'
      ],
      porQue:
        'Los espacios libres deciden si los circuitos nuevos caben o hay que cambiar el ' +
        'centro de carga, que es una partida completa. Y el neutro unido a la tierra en un ' +
        'tablero derivado —no en la acometida— es un hallazgo grave: hace que la corriente ' +
        'de retorno circule por la tierra y por las carcasas de los aparatos.',
      decir: 'Le voy a dejar el tablero documentado. Hoy nadie sabe qué apaga qué, y eso es ' +
             'lo primero que se necesita en una emergencia.',
      captura: 'Espacios, pastillas y su amperaje, foto del tablero',
      ojo: 'No metas los dedos ni un desarmador dentro del tablero. Se fotografía y se ' +
           'transcribe con la tapa puesta; para abrirlo se corta el principal.'
    },

    {
      id: 'circuitos',
      titulo: 'Identifica qué alimenta cada pastilla',
      instrumento: 'Buscador de circuitos, o dos personas y un radio',
      como: [
        'Baja una sola pastilla.',
        'Recorre la casa y anota qué se apagó: contactos, focos, aparatos.',
        'Márcalo en el plano conforme lo encuentres.',
        'Sube esa pastilla y repite con la siguiente.'
      ],
      porQue:
        'Sin este mapa no hay balanceo posible, porque no se sabe qué carga cuelga de qué ' +
        'fase, y no se puede saber cuál circuito está sobrecargado. Es el paso más tedioso ' +
        'y el que más se salta — por eso el sistema lo lleva de la mano. Además es, por sí ' +
        'solo, un entregable que el cliente valora: sale de la visita con su tablero ' +
        'identificado aunque no contrate nada.',
      decir: 'Estoy levantando qué controla cada interruptor. Al final le dejo el tablero ' +
             'etiquetado, tenga o no obra conmigo.',
      captura: 'Mapa de circuito por salida'
    },

    {
      id: 'aparatos',
      titulo: 'Cuenta lo que consume, cuarto por cuarto',
      instrumento: null,
      como: [
        'Recorre la casa y ve eligiendo de la lista lo que encuentres.',
        'Los minisplits se anotan por toneladas, no por marca.',
        'Pregunta qué piensa comprar: parrilla de inducción, secadora, cargador de auto.',
        'Si un aparato tiene la placa a la vista, fotografíala.'
      ],
      porQue:
        'De aquí sale la carga instalada, y de la carga sale el tamaño de la acometida, el ' +
        'número de circuitos y el calibre de cada uno. No hace falta saber cuántos watts ' +
        'consume nada: el sistema los conoce. Lo que sí hace falta es no olvidar un ' +
        'aparato, porque un circuito que no se cotizó se paga de la utilidad.',
      decir: 'Estoy contando todo lo que consume para que el sistema quede dimensionado ' +
             'para su casa real, no para una casa promedio.',
      captura: 'Aparatos por zona, con cantidad',
      ojo: 'Lo que el cliente PIENSA comprar entra al cálculo. Dimensionar solo para lo que ' +
           'hay hoy es condenar la instalación a quedarse corta en dos años, y quien va a ' +
           'quedar mal es quien la hizo.'
    },

    {
      id: 'medicion',
      titulo: 'Mide la instalación',
      instrumento: 'Pinza de valor eficaz verdadero, megóhmetro y cámara termográfica',
      como: [
        'Con la pinza: corriente de cada circuito con la casa en uso normal.',
        'Con el megóhmetro: resistencia de aislamiento entre fase y tierra, y neutro y tierra.',
        'Con la termográfica: temperatura del tablero con carga.',
        'Comprueba continuidad de la tierra física hasta un contacto lejano.'
      ],
      porQue:
        'Esto es lo que separa esta visita de la del electricista que cotiza por teléfono. ' +
        'La prueba de aislamiento detecta la fuga que ningún termomagnético dispara y que ' +
        'puede llevar años corriendo. La termografía convierte un riesgo invisible en una ' +
        'foto que el cliente ve en su propia pantalla, durante la visita.',
      decir: 'Esto que estoy midiendo no se ve y no lo detecta su pastilla. Es corriente que ' +
             'se está yendo a tierra las veinticuatro horas, esté usted o no en casa.',
      captura: 'Mediciones y hallazgos, con foto'
    },

    {
      id: 'planta',
      titulo: 'Traza la planta y marca las salidas',
      instrumento: 'Flexómetro o medidor láser',
      como: [
        'Traza el contorno de la casa igual que un jardín: esquina por esquina.',
        'Marca dónde está el centro de carga: de ahí sale todo el cableado.',
        'Marca los contactos y apagadores que ya existen.',
        'Marca dónde quiere el cliente los nuevos.'
      ],
      porQue:
        'Los metros de cable salen de este trazo, y el cable es de las partidas más grandes ' +
        'del presupuesto. El recorrido no es la línea recta entre dos puntos: va por muros, ' +
        'en ángulo recto, y baja hasta cada contacto. Esa bajada vertical es lo que todo el ' +
        'mundo olvida y puede ser la tercera parte del total.',
      decir: 'Le estoy levantando el plano de su instalación. Con esto el presupuesto sale ' +
             'de metros medidos, no de un estimado a ojo.',
      captura: 'Planta, centro de carga y salidas'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y revisa antes de enseñar',
      instrumento: null,
      como: [
        'Toca «Calcular la instalación».',
        'Revisa que el desequilibrio entre fases quede por debajo del 10%.',
        'Revisa que ningún circuito se pase del 3% de caída de tensión.',
        'Mira qué quedó marcado como sin calibrar y dilo en la propuesta.'
      ],
      porQue:
        'El sistema reparte los circuitos entre las dos fases, elige el calibre de cada uno ' +
        'por el criterio más exigente entre ampacidad y caída de tensión, aplica los topes ' +
        'de protección de la norma, y saca metros de cable y diámetro de tubo. Cada número ' +
        'puede mostrar de qué medición y de qué artículo salió.',
      decir: 'Su casa necesita este número de circuitos, repartidos así entre las dos fases. ' +
             'No es una opinión mía: sale de lo que medimos hoy aquí.',
      captura: 'Cuadro de cargas, balanceo y lista de materiales'
    }
  ]
}



/** Todas las guías, por id de rubro. Hoy solo riego; la estructura ya admite las demás. */
export const GUIAS = { riego: GUIA_RIEGO, electrico: GUIA_ELECTRICA }

/** Convierte lo observable en lo técnico. Es literalmente el Principio 7 en código. */
/* ============================================================================
   VIDEOVIGILANCIA
   ========================================================================== */

/**
 * La guía del rubro donde el cliente pide una cosa y necesita otra.
 *
 * Casi todos piden «cámaras para ver el frente». Lo que necesitan es **identificar** en la
 * puerta y **detectar** en la barda, que son cámaras distintas en lugares distintos. El
 * paso 2 existe sólo para tener esa conversación antes de dibujar nada: si se dibuja
 * primero, la plática se vuelve una negociación de precio en vez de una de alcance.
 */
export const GUIA_CAMARAS = {
  id: 'camaras',
  titulo: 'Videovigilancia',
  duracion: '40 a 50 minutos',
  resumen: 'Se acuerda qué hay que poder ver en cada zona, se traza el predio y se colocan ' +
           'y apuntan las cámaras. De ahí salen solos los metros de UTP, el switch, el ' +
           'grabador y el disco.',

  pasos: [
    {
      id: 'recorrido',
      titulo: 'Camina el predio con el cliente',
      instrumento: null,
      como: [
        'Recorre la propiedad por fuera y por dentro, con el cliente al lado.',
        'Pregúntale qué le preocupa: la reja, la cochera, el acceso de servicio, la alberca.',
        'Fíjate por dónde entraría alguien que no debería: bardas bajas, techos vecinos, andadores oscuros.',
        'Anota dónde hay luz de noche y dónde no.'
      ],
      porQue:
        'Una cámara resuelve un problema concreto, no «da seguridad» en abstracto. Si no se ' +
        'sabe qué hay que poder ver, se acaba poniendo cámaras donde es fácil montarlas en ' +
        'vez de donde sirven. Y la luz importa tanto como el lente: de noche, sin ' +
        'iluminación, la mejor cámara del mercado entrega una silueta.',
      decir: 'Antes de hablar de cámaras quiero entender qué le quita el sueño. ¿Qué es lo ' +
             'que le gustaría poder ver con detalle si algo pasa?',
      ojo: 'Si el cliente dice «que se vea todo», eso no es un requisito: es un presupuesto ' +
           'infinito. Aterrízalo zona por zona en el paso siguiente.'
    },

    {
      id: 'nivel',
      titulo: 'Acuerda qué hay que poder ver en cada zona',
      instrumento: null,
      como: [
        'Explícale los cuatro niveles con el ejemplo de la entrada de su casa.',
        'Para la puerta y la reja, casi siempre es identificación: reconocer a un desconocido.',
        'Para patios y pasillos suele bastar observación: ver qué trae y qué hace.',
        'Para la barda y el perímetro, detección: saber que algo se movió.',
        'Escribe el nivel acordado por zona antes de dibujar nada.'
      ],
      porQue:
        'Es la decisión más cara de todo el levantamiento, y se toma en dos minutos de ' +
        'plática. Identificar exige diez veces más píxeles por metro que detectar, así que ' +
        'una cámara identifica a 6 metros y detecta a 67. Prometer identificación en todo ' +
        'el predio multiplica el número de cámaras; prometer detección donde hacía falta ' +
        'identificación entrega un sistema que grabó al ladrón y no sirve para señalarlo.',
      decir: 'Hay cuatro niveles de detalle. Con el más básico usted sabe que alguien pasó. ' +
             'Con el más alto puede identificar a un desconocido, que es lo que sirve ante ' +
             'un ministerio público. Cuestan distinto porque necesitan cámaras distintas. ' +
             '¿Dónde necesita el nivel alto y dónde le basta con enterarse?',
      ojo: 'La identificación se mide a la ALTURA DE LA CARA, no del piso. Una cámara alta ' +
           'apuntando hacia abajo identifica coronillas.'
    },

    {
      id: 'trazo',
      titulo: 'Traza el predio',
      instrumento: 'Flexómetro, cinta larga o medidor láser',
      como: [
        'Recorre el perímetro de lo que se va a cubrir.',
        'Ve tocando las esquinas en la pantalla, en orden.',
        'Cada lado te dice cuánto mide mientras lo trazas: compáralo con el flexómetro.',
        'Cierra el trazo cuando termines.'
      ],
      porQue:
        'El plano no es sólo insumo de cálculo: es lo que le enseñas al cliente. Ver su ' +
        'propiedad dibujada con los conos encima convierte una cotización en un proyecto.',
      decir: 'Voy a levantar el plano de su predio para que vea exactamente qué queda ' +
             'cubierto y qué no.'
    },

    {
      id: 'rack',
      titulo: 'Ubica dónde va el grabador',
      instrumento: null,
      como: [
        'Busca un lugar cerrado, ventilado y con corriente: closet, cuarto de servicio, bajo escalera.',
        'Que NO sea el primer lugar donde buscaría alguien que entra a robar.',
        'Toca el plano donde iría.'
      ],
      porQue:
        'De ahí sale todo el cable, y el cobre tiene un límite duro de 100 metros por ' +
        'enlace. Un grabador mal ubicado deja cámaras inalcanzables y obliga a meter un ' +
        'switch intermedio o fibra, que se cotiza aparte.',
      decir: 'El grabador conviene ponerlo escondido. Si se llevan el grabador, se llevan ' +
             'la evidencia.',
      ojo: 'Si el cliente pide grabación en la nube además del disco, eso depende de su ' +
           'internet de subida. Mídelo antes de prometerlo.'
    },

    {
      id: 'colocar',
      titulo: 'Coloca y apunta las cámaras',
      instrumento: null,
      como: [
        'Toca el plano donde iría cada cámara. Puede ser sobre la barda o la fachada.',
        'Arrastra el punto blanco del eje para girarla hacia donde debe mirar.',
        'Fíjate en los anillos: el más oscuro es donde identificas, el más claro donde apenas detectas.',
        'Mueve la cámara hasta que el anillo oscuro caiga sobre lo que acordaron en el paso 2.'
      ],
      porQue:
        'Apuntar no es un detalle de instalación: es la mitad del diseño. Una cámara mal ' +
        'orientada se ve idéntica en el plano y cubre el muro de enfrente en vez de la ' +
        'puerta. Por eso el eje se dibuja y se puede girar aquí, y no se deja «para cuando ' +
        'la instalen».',
      decir: 'Mire: aquí, en la zona oscura, usted va a poder identificar una cara. Aquí ' +
             'afuera va a saber que alguien pasó, pero no quién. ¿Le acomoda así o movemos ' +
             'esta cámara para acá?',
      ojo: 'Contraluz. Una cámara apuntando a una ventana o al poniente al atardecer entrega ' +
           'siluetas negras. Si no queda de otra, se cotiza una con WDR real.'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y revisa antes de enseñar',
      instrumento: null,
      como: [
        'Revisa el porcentaje de cada nivel contra lo que acordaron en el paso 2.',
        'Verifica que ningún tramo de cable pase de 100 metros.',
        'Si algo no cuadra, regresa a mover cámaras: es más barato aquí que en obra.'
      ],
      porQue:
        'El sistema reporta los cuatro niveles a la vez, no uno solo. Es lo que permite ' +
        'decir «su jardín queda detectado completo, pero identificado sólo en la puerta», ' +
        'que es exactamente la frase que evita un reclamo seis meses después.',
      decir: 'Esto es lo que queda cubierto y con qué detalle. Si quiere subir el nivel en ' +
             'alguna zona, le digo ahorita cuánto cuesta esa decisión.'
    }
  ]
}

/* ============================================================================
   WI-FI
   ========================================================================== */

/**
 * La guía del rubro donde el cliente ya sabe cuál es su problema —«no me llega»— pero
 * casi siempre se equivoca de causa.
 *
 * Lo que la gente cree que le falta es potencia, y lo que suele faltarle es **otro punto
 * de acceso** o **menos interferencia**. Por eso el paso de medir el ruido va antes de
 * calcular: si el piso de ruido está sucio, poner un equipo más potente empeora las cosas,
 * y ésa es una recomendación que sólo se puede dar con el número en la mano.
 */
export const GUIA_WIFI = {
  id: 'wifi',
  titulo: 'Wi-Fi y conectividad',
  duracion: '30 a 40 minutos',
  resumen: 'Se cuenta lo que se conecta, se ve de qué son los muros, se traza la planta y ' +
           'se colocan los puntos de acceso. De ahí sale cuántos hacen falta y por qué.',

  pasos: [
    {
      id: 'uso',
      titulo: 'Pregunta qué falla y cuenta lo que se conecta',
      instrumento: null,
      como: [
        'Pregunta dónde no le llega y qué se le corta: video, llamadas, o todo.',
        'Cuenta los aparatos: teléfonos, tabletas, televisiones, cámaras, focos, bocinas, el timbre.',
        'Pregunta si hay alguien que trabaje desde casa y en qué cuarto.',
        'Anota si el internet contratado es de fibra o de cable, y de cuántos megas.'
      ],
      porQue:
        'Una red se dimensiona por dos cosas a la vez: el tamaño de la casa y la cantidad ' +
        'de aparatos. Un departamento chico con cuarenta dispositivos necesita más equipos ' +
        'que una casa grande con diez, y quien sólo mira los metros cuadrados se equivoca ' +
        'en el caso que más se queja.',
      decir: 'Voy a contar todo lo que se conecta, no sólo los celulares. En una casa ' +
             'moderna son muchos más de los que uno cree, y cada uno pide su parte.',
      ojo: 'Si el problema es que el internet contratado es lento, más puntos de acceso NO ' +
           'lo arreglan. Eso se dice antes de cotizar, no después de instalar.'
    },

    {
      id: 'construccion',
      titulo: 'Mira de qué está hecha la casa',
      instrumento: null,
      como: [
        'Toca los muros interiores: si suenan huecos son ligeros, si suenan macizos son de block o concreto.',
        'Cuenta cuántas losas tiene que atravesar la señal para llegar al punto más lejano.',
        'Fíjate en canceles de vidrio con marco metálico y en muros con espejo grande.'
      ],
      porQue:
        'Es lo que más cambia el alcance, y se observa sin instrumento. La recomendación ' +
        'internacional que usa el sistema parametriza justo eso: construcción ligera contra ' +
        'construcción de concreto. En México casi toda la vivienda es de block, así que la ' +
        'respuesta correcta suele ser la pesada aunque el inmueble sea una casa.',
      decir: 'Los muros deciden hasta dónde llega la señal. Los de concreto se la comen ' +
             'mucho más que los de tablaroca, y eso cambia cuántos equipos hacen falta.',
      ojo: 'Una losa de concreto entre plantas cuesta más que varios muros juntos. Si hay ' +
           'planta alta, casi siempre necesita su propio equipo.'
    },

    {
      id: 'trazo',
      titulo: 'Traza la planta',
      instrumento: 'Flexómetro, cinta larga o medidor láser',
      como: [
        'Recorre el perímetro de lo que se va a cubrir, incluido el jardín si lo quiere cubierto.',
        'Ve tocando las esquinas en la pantalla, en orden.',
        'Cierra el trazo cuando termines.'
      ],
      porQue:
        'El plano deja ver los huecos. Un porcentaje de cobertura no le dice nada a nadie; ' +
        'ver que el cuarto del fondo queda fuera del círculo, sí.',
      decir: 'Voy a levantar la planta para enseñarle exactamente hasta dónde llega hoy y ' +
             'hasta dónde llegaría con lo que le proponga.'
    },

    {
      id: 'rack',
      titulo: 'Ubica dónde llega el internet',
      instrumento: null,
      como: [
        'Busca dónde entra el servicio: el módem del proveedor.',
        'Toca el plano en ese punto.',
        'Fíjate si hay corriente cerca y si el lugar está ventilado.'
      ],
      porQue:
        'De ahí sale el cable a cada punto de acceso, y el cobre tiene un límite duro de ' +
        '100 metros por enlace. Además, donde está el módem casi nunca es el mejor lugar ' +
        'para el Wi-Fi: suele ser una esquina de la casa, y desde una esquina se desperdicia ' +
        'la mitad de la cobertura hacia la calle.',
      decir: 'El módem lo puso el proveedor donde le quedó cómodo, no donde conviene para ' +
             'la señal. Por eso los puntos de acceso van en otro lado y se conectan con cable.'
    },

    {
      id: 'colocar',
      titulo: 'Coloca los puntos de acceso',
      instrumento: null,
      como: [
        'Toca el plano donde iría cada equipo. Al centro de la zona que debe cubrir, no en la esquina.',
        'Si hay planta alta o jardín, ponles el suyo: la señal no da la vuelta.',
        'Arrastra para acomodar y mira cómo se mueve el círculo de cobertura.'
      ],
      porQue:
        'Un punto de acceso irradia en todas direcciones. Ponerlo pegado a un muro exterior ' +
        'regala la mitad de su alcance a la calle. Al centro de su zona cubre el doble de ' +
        'superficie útil con el mismo equipo.',
      decir: 'Cada equipo cubre un círculo. Los acomodo para que los círculos se traslapen ' +
             'un poco y usted pueda caminar por la casa sin que se le corte la llamada.',
      ojo: 'No los pongas dentro de un closet metálico ni detrás de la televisión. Suena ' +
           'obvio y es donde acaban la mitad de los equipos instalados.'
    },

    {
      id: 'medir',
      titulo: 'Mide el ruido',
      instrumento: 'Analizador de Wi-Fi en el celular',
      como: [
        'Abre el analizador y busca la lectura de piso de ruido, en dBm.',
        'Mídelo en el cuarto donde el cliente reporta el problema, no en la entrada.',
        'Anota también cuántas redes de vecinos aparecen y en qué canales.'
      ],
      porQue:
        'Es el único número de todo este levantamiento que NO es una estimación, y es el ' +
        'que más manda. Lo que decide si una señal sirve no es cuántos dBm llegan sino ' +
        'cuánto sobresalen del ruido. Con ruido alto, un equipo más potente **empeora** el ' +
        'problema en vez de arreglarlo, porque el vecino sube el suyo y acaban gritándose.',
      decir: 'Voy a medir cuánto ruido hay de otras redes. Si hay mucho, el problema no se ' +
             'arregla con más potencia y le voy a decir por qué.',
      ojo: 'Si el piso de ruido sale peor de -80 dBm, la conversación cambia: eso es un ' +
           'problema de canales y de vecinos, y se resuelve con planeación, no comprando ' +
           'el equipo más caro.'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y revisa antes de enseñar',
      instrumento: null,
      como: [
        'Escribe los metros construidos.',
        'Revisa cuál de los dos criterios manda: cobertura o capacidad.',
        'Si faltan equipos por colocar, regresa y ponlos para ver dónde caen.'
      ],
      porQue:
        'El sistema propone el mayor de dos cuentas, no el promedio. Saber cuál manda es lo ' +
        'que permite explicarle al cliente por qué su departamento chico necesita tres ' +
        'equipos: no es el tamaño, son los aparatos.',
      decir: 'Esto es lo que su casa pide y por qué. Es una estimación: el día de la ' +
             'instalación lo medimos y acomodamos si hace falta, sin costo extra.'
    }
  ]
}

/* ============================================================================
   ILUMINACIÓN Y AUTOMATIZACIÓN
   ========================================================================== */

/**
 * La guía del rubro donde el cliente ya tiene una opinión formada, y casi siempre es la
 * equivocada: pide «más luz» cuando lo que le falta es **luz donde hace la tarea**.
 *
 * Una cocina con un plafón central potente sigue teniendo la barra en sombra, porque el
 * cuerpo del que cocina tapa su propia luz. El paso 2 existe para cambiar esa
 * conversación antes de que se convierta en una lista de lámparas.
 *
 * El paso 1 pregunta si ahí trabaja alguien contratado, y no es burocracia: es lo que
 * decide si el número es exigible por ley o negociable con el cliente.
 */
export const GUIA_ILUMINACION = {
  id: 'iluminacion',
  titulo: 'Iluminación y control',
  duracion: '35 a 45 minutos',
  resumen: 'Se mide cada espacio, se acuerda qué se hace en él y cómo quiere controlarlo. ' +
           'De ahí salen las luminarias, los módulos de control y el porqué de cada nivel.',

  pasos: [
    {
      id: 'regimen',
      titulo: 'Averigua si algún espacio es centro de trabajo',
      instrumento: null,
      como: [
        'Pregunta si en la casa trabaja alguien contratado y en qué cuarto.',
        'Ojo con el estudio, la cocina si hay personal, y el taller o cuarto de servicio.',
        'Si el inmueble es un local, una oficina o un taller, TODO es centro de trabajo.'
      ],
      porQue:
        'Cambia el régimen legal, no el gusto. En un centro de trabajo la NOM-025-STPS-2008 ' +
        'exige un nivel mínimo que una inspección puede medir con luxómetro, y quedarse ' +
        'abajo es un incumplimiento. En una vivienda no existe esa norma: el nivel es una ' +
        'recomendación de confort que se acuerda. Decir «esto cumple norma» en una sala es ' +
        'prometer algo que no existe.',
      decir: 'Le pregunto porque si aquí trabaja alguien contratado, hay niveles de luz que ' +
             'la ley exige y que se pueden medir. Si es sólo para ustedes, lo ajustamos a ' +
             'lo que les acomode.',
      ojo: 'Una oficina en casa donde trabaja el propio dueño no es centro de trabajo. Lo ' +
           'que lo vuelve centro de trabajo es que haya alguien contratado.'
    },

    {
      id: 'tarea',
      titulo: 'Pregunta qué se hace en cada cuarto, no cuánta luz quiere',
      instrumento: null,
      como: [
        'Recorre cuarto por cuarto con el cliente.',
        'Pregunta qué actividad concreta pasa ahí y EN QUÉ SUPERFICIE: la barra, la mesa, el espejo, la cabecera.',
        'Fíjate dónde hay sombra hoy, sobre todo donde alguien se para a trabajar.',
        'Anota los acabados: un cuarto de muros oscuros necesita más luz que uno claro.'
      ],
      porQue:
        'La luz no se diseña para un cuarto: se diseña para lo que ahí se hace. Una cocina ' +
        'con un solo plafón al centro deja la barra en sombra, porque quien cocina tapa su ' +
        'propia luz con el cuerpo. Preguntar «cuánta luz quiere» lleva a llenar el techo de ' +
        'lámparas; preguntar «qué hace aquí» lleva a poner luz donde sirve.',
      decir: 'No le voy a preguntar cuánta luz quiere. Dígame qué hace en cada cuarto y ' +
             'sobre qué superficie, y de ahí sale cuánta luz necesita y dónde.',
      ojo: 'El espejo del baño se ilumina DE FRENTE, no desde arriba. La luz cenital hace ' +
           'sombra en los ojos y bajo la nariz: es el error más común y el más visible.'
    },

    {
      id: 'medir',
      titulo: 'Mide cada espacio',
      instrumento: 'Flexómetro o medidor láser',
      como: [
        'Mide largo y ancho de cada cuarto.',
        'Mide la altura del techo, y réstale la altura del plano donde se trabaja: la barra va a 0.90 m, el escritorio a 0.75, el piso a 0.',
        'Si ya hay luz instalada, mídela con el luxómetro: es el antes que justifica el después.'
      ],
      porQue:
        'Las dimensiones deciden cuántas luminarias hacen falta, y la altura decide cuánta ' +
        'de esa luz llega al plano. Un cuarto alto y angosto desperdicia buena parte en los ' +
        'muros; uno bajo y ancho la aprovecha casi toda.',
      decir: 'Con la medida y la altura puedo calcular exactamente cuántas luminarias hacen ' +
             'falta, en vez de ponerlas a ojo y esperar que alcance.',
      ojo: 'La medición del ANTES es el mejor argumento de venta del rubro. Un número medido ' +
           'de 90 lux en una cocina convence más que cualquier explicación.'
    },

    {
      id: 'control',
      titulo: 'Acuerda cómo quiere controlar cada espacio',
      instrumento: null,
      como: [
        'Pregunta qué le molesta hoy: levantarse a apagar, no encontrar el apagador, dejar luces prendidas.',
        'Pasillos, clósets y baños de visita casi siempre piden presencia.',
        'Sala, comedor y recámara son donde la atenuación se disfruta de verdad.',
        'Un cuarto con dos entradas necesita conmutado, y eso es cableado, no automatización.'
      ],
      porQue:
        'Cada tipo de control resuelve un problema distinto. Vender atenuación donde hacía ' +
        'falta un apagador de escalera es caro y no arregla nada. Y el módulo se cuenta por ' +
        'ESPACIO, no por lámpara: un cuarto con doce luminarias lleva un módulo, no doce.',
      decir: 'La automatización no es prender todo desde el celular. Es que el pasillo se ' +
             'encienda solo cuando pasa alguien y que la sala baje de intensidad para ver ' +
             'una película. Dígame qué le molesta hoy y de ahí sale qué conviene.',
      ojo: 'Para atenuar, las lámparas tienen que ser atenuables y el módulo pide una carga ' +
           'mínima. Con una sola lámpara LED chica es muy probable que parpadee, y el cliente ' +
           'lo va a reportar como falla de la automatización.'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y revisa antes de enseñar',
      instrumento: null,
      como: [
        'Revisa espacio por espacio que la cantidad de luminarias tenga sentido.',
        'Si sale un número que no te cuadra, cambia el flujo de la luminaria antes que el nivel: menos lámparas más potentes suele ser mejor y más barato.',
        'Verifica que los espacios de centro de trabajo lleguen a su mínimo de norma.'
      ],
      porQue:
        'El cálculo usa un coeficiente de utilización de trabajo, no el de la tabla ' +
        'fotométrica de la luminaria concreta. Da una cantidad correcta en orden de ' +
        'magnitud y hay que mirarla con criterio: es una propuesta, no un cálculo ' +
        'luminotécnico certificado.',
      decir: 'Esto es lo que pide cada espacio y por qué. Si quiere menos lámparas se puede, ' +
             'usando unas más potentes: le muestro las dos opciones.'
    }
  ]
}

/* ============================================================================
   FABRICACIÓN EN MADERA
   ========================================================================== */

/**
 * La guía del rubro donde **el error caro no es de cálculo: es de medición**.
 *
 * Un circuito mal dimensionado se corrige cambiando un cable. Un mueble mal medido no se
 * corrige: se vuelve a hacer, y la placa ya se cortó. Por eso esta guía dedica dos pasos
 * enteros a medir y a comprobar el muro, y por qué el paso de los servicios existe —el
 * mueble que choca con la toma de gas es el clásico del oficio.
 *
 * Y por eso el paso final es de **expectativa**, no de número: en este rubro el cliente
 * compara por metro lineal contra presupuestos que incluyen cosas distintas, y esa
 * conversación se gana enseñando qué entra y qué no, no bajando el precio.
 */
export const GUIA_TALLER = {
  id: 'taller',
  titulo: 'Fabricación en madera',
  duracion: '40 a 60 minutos',
  resumen: 'Se mide el hueco, se comprueba que el muro esté a escuadra, y se arma el ' +
           'mueble por módulos. De ahí salen las placas, los herrajes y los minutos de máquina.',

  pasos: [
    {
      id: 'uso',
      titulo: 'Pregunta qué va a guardar, no qué mueble quiere',
      instrumento: null,
      como: [
        'Pregunta qué guarda hoy en esa zona y qué le falta acomodar.',
        'Ojo con lo alto: ollas grandes, licuadora, garrafón. Eso decide alturas.',
        'Pregunta quién lo usa: si hay alguien de baja estatura o con problemas de espalda, los cajones dejan de ser lujo.',
        'Anota si tiene electrodomésticos que tengan que quedar empotrados, con marca y modelo.'
      ],
      porQue:
        'La diferencia entre un mueble bueno y uno caro está en el reparto, no en el ' +
        'material. Un bajo de puertas cuesta la mitad que uno de cajones y guarda lo ' +
        'mismo — pero para sacar la olla de hasta atrás hay que meterse de rodillas. ' +
        'Preguntar qué guarda lleva a poner cajones donde se usan y puertas donde no.',
      decir: 'Antes de hablar de medidas, dígame qué guarda aquí y qué no le cabe hoy. De ' +
             'ahí sale si conviene cajón o puerta, que es lo que más cambia el precio y el uso.',
      ojo: 'Si hay electrodoméstico empotrado, pide marca y modelo y busca su ficha. Cada ' +
           'uno pide un hueco distinto y **no se puede improvisar en obra**.'
    },

    {
      id: 'medir',
      titulo: 'Mide el hueco en tres alturas',
      instrumento: 'Flexómetro y medidor láser',
      como: [
        'Mide el ancho del hueco ABAJO, A LA MITAD y ARRIBA. Anota los tres.',
        'Mide la altura en los dos extremos y al centro.',
        'Mide el fondo disponible, contando el zoclo y el rodapié si los hay.',
        'Usa siempre la medida MENOR de las tres: es la que tiene que pasar.'
      ],
      porQue:
        'Ningún muro es recto. La diferencia entre la medida de arriba y la de abajo suele ' +
        'ser de uno o dos centímetros, y a veces más en obra vieja. Un mueble hecho a la ' +
        'medida mayor no entra, y el material ya se cortó: en este rubro el error de ' +
        'medición no se corrige, se vuelve a fabricar.',
      decir: 'Voy a medir en varios puntos porque los muros nunca están perfectamente ' +
             'rectos. Es lo que evita que el mueble llegue y no entre.',
      ojo: 'Anota la diferencia entre la mayor y la menor. Si pasa de 15 mm, el mueble ' +
           'necesita un **suplemento de ajuste** y eso se cotiza: no se absorbe.'
    },

    {
      id: 'escuadra',
      titulo: 'Comprueba escuadra, plomo y nivel',
      instrumento: 'Nivel de burbuja y escuadra, o láser de líneas',
      como: [
        'Pon el nivel en el piso a lo largo del hueco: si cae, anota hacia dónde y cuánto.',
        'Pon el nivel vertical en las esquinas: si el muro se va, anota cuánto.',
        'Comprueba la escuadra de la esquina con el método 3-4-5, o con la escuadra grande.',
        'Fotografía cualquier tubería, registro o contacto que quede dentro del hueco.'
      ],
      porQue:
        'Un piso que cae hace que un mueble a nivel deje una cuña visible contra el zoclo, ' +
        'y un muro fuera de plomo hace que la puerta no cierre pareja. Se resuelve con ' +
        'niveladores y con un zoclo de ajuste, pero hay que saberlo ANTES: descubrirlo el ' +
        'día de la instalación convierte una entrega en un reclamo.',
      decir: 'Estoy revisando qué tan a escuadra está el muro. Si tiene desnivel se ' +
             'resuelve, pero prefiero saberlo hoy y no el día que traigamos el mueble.',
      ojo: 'La toma de gas, el registro de agua y el contacto son lo que más choca. Si ' +
           'quedan detrás de un mueble, hay que dejar registro o moverlos, y eso es otra ' +
           'partida.'
    },

    {
      id: 'modulos',
      titulo: 'Arma el mueble por módulos',
      instrumento: null,
      como: [
        'Reparte el hueco en módulos: ninguno de más de 90 cm de ancho.',
        'Pon cajones donde se usa a diario y puertas donde se guarda lo de temporada.',
        'Deja el último módulo como el de ajuste: es el que absorbe la diferencia del muro.',
        'Revisa que la suma de los frentes sea menor que la medida MENOR del hueco.'
      ],
      porQue:
        'Los muebles se fabrican por módulos porque una caja de más de 90 cm se pandea, no ' +
        'cabe en la placa sin desperdicio, y no la carga una persona. Además el módulo es ' +
        'la unidad que se puede contar sin saber carpintería, y la que después se convierte ' +
        'sola en placas, cortes y herrajes.',
      decir: 'Lo voy a repartir en módulos. Así se fabrica mejor, se transporta y, si algún ' +
             'día quiere cambiar una parte, no hay que tocar todo lo demás.',
      ojo: 'La suma de los frentes NUNCA debe ser igual al hueco. Deja al menos 10 mm de ' +
           'holgura total, o el último mueble entra a martillazos.'
    },

    {
      id: 'calcular',
      titulo: 'Calcula y acuerda el alcance',
      instrumento: null,
      como: [
        'Revisa el costo por metro y compáralo con lo que el cliente haya visto en otro lado.',
        'Enséñale la lista de qué incluye y qué no. Es la conversación que cierra.',
        'Confirma si quiere la cubierta y la instalación, que se cotizan aparte.'
      ],
      porQue:
        'El cliente va a comparar por metro lineal contra presupuestos que incluyen cosas ' +
        'distintas, y casi siempre el más barato trae menos. Esa discusión no se gana ' +
        'bajando el precio: se gana enseñando la lista. Un presupuesto que dice qué NO ' +
        'incluye es más creíble que uno que promete todo.',
      decir: 'Le doy el precio por metro para que pueda comparar, y también la lista exacta ' +
             'de qué incluye. Si le pasan uno más barato, comparemos las listas: casi ' +
             'siempre ahí está la diferencia.'
    }
  ]
}

export const conversiones = {
  /** Cubeta y cronómetro → litros por minuto. */
  caudal: (litrosCubeta, segundos) =>
    !segundos ? 0 : (litrosCubeta / segundos) * 60
}
